import {
  createContext,
  useContext,
  useState,
  useMemo,
  FC,
  PropsWithChildren,
  useEffect,
} from "react";

import { DropdownOptionI } from "shared/ui/user-input/dropdown";
import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { useFetchCampaignCustomDispositions } from "@/modules/pipeline/account-details/data/queries";
import { CustomDispositionI } from "shared/lib/interfaces/dispositions";

export const DISPOSITION_OPTION_ID_SEPARATOR = "--";

interface DispositionSectionContextI {
  customDispositions?: CustomDispositionI[];
  dispositionOptions: DropdownOptionI[];
  setDispositionOptions: (options: DropdownOptionI[]) => void;
}

const DispositionSectionContext = createContext<
  DispositionSectionContextI | undefined
>(undefined);

interface DispositionSectionContextProviderProps extends PropsWithChildren {
  initialDispositionOptions?: DropdownOptionI[];
}

export const DispositionSectionContextProvider: FC<
  DispositionSectionContextProviderProps
> = ({ children }) => {
  const [dispositionOptions, setDispositionOptions] = useState<
    DropdownOptionI[]
  >([]);

  const { campaign } = useAccountDetailsContext();

  const { data: customDispositions } = useFetchCampaignCustomDispositions(
    campaign?.id as string
  );

  useEffect(() => {
    if (customDispositions) {
      setDispositionOptions(
        customDispositions.map((dispo) => ({
          label: dispo.name,
          value: `${dispo.id}${DISPOSITION_OPTION_ID_SEPARATOR}${dispo.key}${DISPOSITION_OPTION_ID_SEPARATOR}${dispo.status}`,
        }))
      );
    }
  }, [customDispositions]);

  const ctx: DispositionSectionContextI = useMemo(
    () => ({ customDispositions, dispositionOptions, setDispositionOptions }),
    [dispositionOptions, customDispositions]
  );

  return (
    <DispositionSectionContext.Provider value={ctx}>
      {children}
    </DispositionSectionContext.Provider>
  );
};

export const useDispositionSectionContext = (): DispositionSectionContextI => {
  const context = useContext(DispositionSectionContext);
  if (context === undefined) {
    throw new Error(
      "useDispositionSectionContext must be used within a DispositionSectionContextProvider"
    );
  }
  return context;
};
